// File: frontend/src/components/Pipeline/PipelineColumn.jsx
// Enhanced Kanban column with visual metrics and professional styling

import React from 'react';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { 
  CurrencyDollarIcon,
  ChartBarIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  PlusIcon,
  ArrowsRightLeftIcon
} from '@heroicons/react/24/outline';
import OpportunityCard from './OpportunityCard';

const PipelineColumn = ({
  stage,
  opportunities = [],
  metrics,
  onCreateNew,
  onEditOpportunity,
  isDragDisabled = false
}) => {
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  // Get stage-specific colors
  const getStageColors = (stageKey) => {
    const colors = {
      lead: {
        header: 'bg-gray-600 dark:bg-gray-700',
        headerText: 'text-white',
        bg: 'bg-gray-50 dark:bg-gray-900',
        border: 'border-gray-200 dark:border-gray-700',
        accent: 'text-gray-600'
      },
      qualified: {
        header: 'bg-blue-600 dark:bg-blue-700',
        headerText: 'text-white',
        bg: 'bg-blue-50 dark:bg-blue-950',
        border: 'border-blue-200 dark:border-blue-800',
        accent: 'text-blue-600'
      },
      proposal: {
        header: 'bg-yellow-600 dark:bg-yellow-700',
        headerText: 'text-white',
        bg: 'bg-yellow-50 dark:bg-yellow-950',
        border: 'border-yellow-200 dark:border-yellow-800',
        accent: 'text-yellow-600'
      },
      negotiation: {
        header: 'bg-orange-600 dark:bg-orange-700',
        headerText: 'text-white',
        bg: 'bg-orange-50 dark:bg-orange-950',
        border: 'border-orange-200 dark:border-orange-800',
        accent: 'text-orange-600'
      },
      closed_won: {
        header: 'bg-green-600 dark:bg-green-700',
        headerText: 'text-white',
        bg: 'bg-green-50 dark:bg-green-950',
        border: 'border-green-200 dark:border-green-800',
        accent: 'text-green-600'
      },
      closed_lost: {
        header: 'bg-red-600 dark:bg-red-700',
        headerText: 'text-white',
        bg: 'bg-red-50 dark:bg-red-950',
        border: 'border-red-200 dark:border-red-800',
        accent: 'text-red-600'
      }
    };
    return colors[stageKey] || colors.lead;
  };

  const colors = getStageColors(stage.key);

  // Calculate win probability for display
  const getWinProbability = (stageKey) => {
    const probabilities = {
      lead: 10,
      qualified: 25,
      proposal: 50,
      negotiation: 75,
      closed_won: 100,
      closed_lost: 0
    };
    return probabilities[stageKey] || 0;
  };

  return (
    <div className={`flex flex-col h-full w-80 min-w-80 flex-shrink-0 ${colors.bg} rounded-lg border ${colors.border} shadow-sm`}>
      {/* Column Header */}
      <div className={`${colors.header} rounded-t-lg p-4`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`font-semibold text-lg ${colors.headerText}`}>
              {stage.label}
            </h3>
            <div className="flex items-center mt-1 space-x-3">
              <span className={`text-sm ${colors.headerText} opacity-90`}>
                {metrics.count} oportunidades
              </span>
              <span className={`text-sm ${colors.headerText} opacity-90`}>
                {getWinProbability(stage.key)}% prob.
              </span>
            </div>
          </div>
          
          <button
            onClick={() => onCreateNew(stage.key)}
            className={`p-2 rounded-md ${colors.headerText} hover:bg-white hover:bg-opacity-20 transition-colors`}
            title="Adicionar oportunidade"
          >
            <PlusIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Enhanced Metrics Row */}
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="bg-white bg-opacity-20 rounded-lg p-2">
            <div className="flex items-center">
              <CurrencyDollarIcon className={`h-4 w-4 ${colors.headerText} mr-2`} />
              <div>
                <p className={`text-xs ${colors.headerText} opacity-75`}>
                  Valor Total
                </p>
                <p className={`text-sm font-semibold ${colors.headerText}`}>
                  {formatCurrency(metrics.totalValue)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white bg-opacity-20 rounded-lg p-2">
            <div className="flex items-center">
              <ChartBarIcon className={`h-4 w-4 ${colors.headerText} mr-2`} />
              <div>
                <p className={`text-xs ${colors.headerText} opacity-75`}>
                  Ticket Médio
                </p>
                <p className={`text-sm font-semibold ${colors.headerText}`}>
                  {formatCurrency(metrics.avgValue)}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Valor ponderado pela probabilidade da etapa */}
        <div className="mt-2 bg-white bg-opacity-10 rounded-lg p-2">
          <div className="flex items-center">
            <ArrowsRightLeftIcon className={`h-4 w-4 ${colors.headerText} mr-2`} />
            <div>
              <p className={`text-xs ${colors.headerText} opacity-75`}>
                Valor Ponderado ({getWinProbability(stage.key)}% prob.)
              </p>
              <p className={`text-sm font-semibold ${colors.headerText}`}>
                {formatCurrency((metrics.totalValue || 0) * getWinProbability(stage.key) / 100)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Droppable Area */}
      <div className="flex-1 p-4 overflow-hidden">
        <Droppable droppableId={stage.key}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`h-full space-y-3 overflow-y-auto ${
                snapshot.isDraggingOver 
                  ? 'bg-white bg-opacity-50 dark:bg-gray-800 dark:bg-opacity-50 rounded-lg' 
                  : ''
              } transition-colors duration-200`}
              style={{ minHeight: '200px' }}
            >
              {opportunities.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-center">
                  <div className={`${colors.accent} opacity-50`}>
                    <ChartBarIcon className="h-12 w-12 mx-auto mb-2" />
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Nenhuma oportunidade
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Arraste ou crie uma nova
                  </p>
                </div>
              ) : (
                opportunities.map((opportunity, index) => (
                  <Draggable
                    key={opportunity.id}
                    draggableId={opportunity.id.toString()}
                    index={index}
                    isDragDisabled={isDragDisabled}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          transform: snapshot.isDragging 
                            ? `${provided.draggableProps.style?.transform} rotate(3deg)`
                            : provided.draggableProps.style?.transform
                        }}
                        className={`${
                          snapshot.isDragging 
                            ? 'shadow-xl ring-2 ring-indigo-500 ring-opacity-50 scale-105' 
                            : 'shadow-sm hover:shadow-md'
                        } transition-all duration-200`}
                      >
                        <OpportunityCard
                          opportunity={opportunity}
                          onEdit={onEditOpportunity}
                          stageColor={colors.accent}
                          isDragging={snapshot.isDragging}
                        />
                      </div>
                    )}
                  </Draggable>
                ))
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>

      {/* Column Footer with Quick Stats */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-b-lg">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center space-x-4">
            <span className="flex items-center">
              <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
              {getWinProbability(stage.key)}% probabilidade
            </span>
            {metrics.count > 0 && (
              <span className="flex items-center">
                <ClockIcon className="h-3 w-3 mr-1" />
                ~{Math.round(metrics.avgValue / 1000)}k médio
              </span>
            )}
          </div>
          <span className="font-medium">
            {metrics.count} itens
          </span>
        </div>
      </div>
    </div>
  );
};

export default PipelineColumn;